/**
 * AppTextarea Component
 *
 * A reusable textarea wrapping Mantine Textarea with a custom label row
 * that displays label text, required asterisk, and character counter.
 * Shares the same visual label pattern as AppTextInput.
 * Handles paste events to trim text at maxLength boundary.
 *
 * @example
 * ```tsx
 * // Basic usage with character counter
 * <AppTextarea
 *   label="Description"
 *   value={description}
 *   onChange={handleChange}
 *   maxLength={500}
 *   rows={4}
 * />
 *
 * // With autosize
 * <AppTextarea
 *   label="Notes"
 *   value={notes}
 *   onChange={handleChange}
 *   autosize
 *   minRows={2}
 *   maxRows={6}
 * />
 *
 * // No label (plain Textarea behavior)
 * <AppTextarea
 *   value={value}
 *   onChange={handleChange}
 *   placeholder="Enter text..."
 * />
 * ```
 */

import type { ClipboardEvent, ChangeEvent } from 'react';
import { memo, useCallback } from 'react';
import { Textarea } from '@mantine/core';

import { InputLabel } from '@/components/common/InputLabel';
import type { AppTextareaProps } from './AppTextarea.types';
import styles from './AppTextarea.module.css';

export const AppTextarea = memo(function AppTextarea({
  label,
  maxLength,
  showCounter,
  required,
  value,
  onChange,
  className,
  onPaste,
  ...mantineProps
}: AppTextareaProps) {
  const currentLength = typeof value === 'string' ? value.length : 0;
  const shouldShowCounter = showCounter ?? maxLength !== undefined;

  const labelComponent = label ? (
    <InputLabel
      label={label}
      required={required}
      showCounter={shouldShowCounter}
      currentLength={currentLength}
      maxLength={maxLength}
    />
  ) : undefined;

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      if (maxLength) {
        const pastedText = event.clipboardData.getData('text');
        const textareaElement = event.target as HTMLTextAreaElement;
        const currentValue = textareaElement.value;
        const selectionStart =
          textareaElement.selectionStart ?? currentValue.length;
        const selectionEnd = textareaElement.selectionEnd ?? selectionStart;
        const newValue =
          currentValue.slice(0, selectionStart) +
          pastedText +
          currentValue.slice(selectionEnd);

        if (newValue.length > maxLength) {
          event.preventDefault();
          const trimmedValue = newValue.slice(0, maxLength);
          textareaElement.value = trimmedValue;
          onChange?.(
            trimmedValue as unknown as ChangeEvent<HTMLTextAreaElement>
          );
        }
      }
      onPaste?.(event);
    },
    [maxLength, onChange, onPaste]
  );

  return (
    <Textarea
      {...mantineProps}
      label={labelComponent}
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      required={required}
      withAsterisk={false}
      onPaste={handlePaste}
      className={className}
      classNames={{ label: styles.fullWidthLabel }}
    />
  );
});

AppTextarea.displayName = 'AppTextarea';
